import React, { useState, useEffect, useRef } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import LogoImg from './LogoImg';
import {
  LayoutDashboard, BookOpen, ShieldCheck, Phone, Gift, Briefcase,
  Compass, Users, Library, Heart, Scale, ChevronLeft,
  ChevronRight, Flame, Coins, LogOut, Sun, Moon, Bell, ClipboardList,
  BarChart2, Settings, Bot, FileText, BellRing, GraduationCap, Store, Mail, Trophy,
} from './CustomIcons';

// VAPID key helper
function urlB64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

// ── Grouped nav definitions per role ─────────────────────────────────────────
// `exact: true` → only activate on exact path match (no startsWith)
// `pinned` item sits above all groups, outside any section label

const GROUPED_NAV = {
  learner: {
    pinned: { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    groups: [
      {
        label: 'LEARNING',
        items: [
          { path: '/learn', label: 'Learn', icon: BookOpen },
          { path: '/credentials', label: 'Credentials', icon: ShieldCheck },
          { path: '/ai-tutor', label: 'AI Counselor', icon: Bot },
          { path: '/library', label: 'Library', icon: Library },
        ],
      },
      {
        label: 'LIFE & REENTRY',
        items: [
          { path: '/reentry', label: 'Reentry', icon: Compass },
          { path: '/health', label: 'Health', icon: Heart },
          { path: '/legal', label: 'Legal', icon: Scale },
        ],
      },
      {
        label: 'CONNECT & EARN',
        items: [
          { path: '/community', label: 'Community', icon: Users },
          { path: '/connect', label: 'Connect', icon: Phone },
          { path: '/rewards', label: 'Rewards', icon: Gift },
          { path: '/jobs', label: 'Jobs', icon: Briefcase },
          { path: '/leaderboard', label: 'Leaderboard', icon: Trophy },
          { path: '/messages', label: 'Messages', icon: Mail },
        ],
      },
      {
        label: 'TOOLS',
        items: [
          { path: '/progress-report', label: 'My Report', icon: FileText },
        ],
      },
    ],
  },

  facility_admin: {
    pinned: { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    groups: [
      {
        label: 'PEOPLE',
        items: [
          { path: '/case-manager', label: 'Caseload', icon: ClipboardList },
        ],
      },
      {
        label: 'CONTENT',
        items: [
          { path: '/marketplace/catalog', label: 'Marketplace', icon: Store },
          { path: '/marketplace/admin', label: 'Mkt Admin', icon: BarChart2 },
        ],
      },
      {
        label: 'HIRING',
        items: [
          { path: '/jobs', label: 'Jobs', icon: Briefcase },
        ],
      },
    ],
  },

  employer_partner: {
    pinned: { path: '/marketplace', label: 'Talent Marketplace', icon: Store, exact: true },
    groups: [
      {
        label: 'MY LISTINGS',
        items: [
          { path: '/jobs/employer', label: 'Job Listings', icon: Briefcase },
        ],
      },
      {
        label: 'MESSAGING',
        items: [
          { path: '/jobs/employer', label: 'Messages', icon: Mail },
        ],
      },
    ],
  },

  provider_partner: {
    pinned: { path: '/marketplace/provider', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    groups: [
      {
        label: 'CONTENT',
        items: [
          { path: '/marketplace/provider/courses', label: 'My Courses', icon: BookOpen },
          { path: '/marketplace/provider/analytics', label: 'Analytics', icon: BarChart2 },
        ],
      },
      {
        label: 'SETTINGS',
        items: [
          { path: '/marketplace/provider/settings', label: 'Settings', icon: Settings },
        ],
      },
    ],
  },

  case_manager: {
    pinned: { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    groups: [
      {
        label: 'PEOPLE',
        items: [
          { path: '/case-manager', label: 'Caseload', icon: ClipboardList },
        ],
      },
      {
        label: 'SUPPORT',
        items: [
          { path: '/reentry', label: 'Reentry', icon: Compass },
          { path: '/community', label: 'Community', icon: Users },
        ],
      },
      {
        label: 'HIRING',
        items: [
          { path: '/jobs', label: 'Jobs', icon: Briefcase },
        ],
      },
    ],
  },

  instructor: {
    pinned: { path: '/instructor', label: 'Class Portal', icon: GraduationCap, exact: true },
    groups: [
      {
        label: 'PEOPLE',
        items: [
          { path: '/case-manager', label: 'Caseload', icon: ClipboardList },
        ],
      },
      {
        label: 'LEARNING',
        items: [
          { path: '/learn', label: 'Courses', icon: BookOpen },
        ],
      },
      {
        label: 'CONNECT',
        items: [
          { path: '/community', label: 'Community', icon: Users },
          { path: '/jobs', label: 'Jobs Board', icon: Briefcase },
        ],
      },
    ],
  },

  youth_learner: {
    pinned: { path: '/dashboard', label: 'Portal Home', icon: LayoutDashboard, exact: true },
    groups: [
      {
        label: 'LEARNING',
        items: [
          { path: '/learn', label: 'Courses', icon: BookOpen },
          { path: '/credentials', label: 'Badges', icon: Trophy },
        ],
      },
      {
        label: 'CONNECT',
        items: [
          { path: '/community', label: 'Community', icon: Users },
          { path: '/rewards', label: 'Rewards', icon: Gift },
        ],
      },
    ],
  },
};

function getGroupedNav(role) {
  return GROUPED_NAV[role] || GROUPED_NAV.learner;
}

function getRoleBadgeStyle(role) {
  const styles = {
    learner: { bg: 'rgba(249,115,22,0.15)', color: '#F97316', border: 'rgba(249,115,22,0.3)', label: 'Learner' },
    facility_admin: { bg: 'var(--bg-elevated)', color: 'var(--text-primary)', border: 'var(--border-default)', label: 'Admin' },
    employer_partner: { bg: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: 'var(--border-default)', label: 'Employer' },
    case_manager: { bg: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: 'var(--border-default)', label: 'Case Mgr' },
    instructor: { bg: 'rgba(34,197,94,0.12)', color: '#22C55E', border: 'rgba(34,197,94,0.25)', label: 'Instructor' },
    provider_partner: { bg: 'rgba(249,115,22,0.12)', color: '#F97316', border: 'rgba(249,115,22,0.25)', label: 'Provider' },
    youth_learner: { bg: 'rgba(139,92,246,0.15)', color: '#A78BFA', border: 'rgba(139,92,246,0.3)', label: 'Youth' },
  };
  return styles[role] || styles.learner;
}

// ── Single nav item ───────────────────────────────────────────────────────────

function SidebarNavItem({ path, label, icon: Icon, collapsed, onClick, exact = false, badgeCount = 0 }) {
  const location = useLocation();
  const isActive = exact
    ? location.pathname === path
    : location.pathname === path ||
      (path.length > 1 && location.pathname.startsWith(path));
  const testId = path === '/learn' ? 'learn-nav-item' : path === '/reentry' ? 'reentry-nav-item' : `nav-${label.toLowerCase().replace(/\s+/g, '-')}`;

  // Pulsing NEW badge for /learn — clears itself when the user lands on /learn
  const [showNewDot, setShowNewDot] = useState(() => {
    if (path !== '/learn') return false;
    return localStorage.getItem('aos_learn_new_badge') !== '0' &&
           localStorage.getItem('aos_new_courses_dismissed') !== '1';
  });
  useEffect(() => {
    if (path === '/learn' && isActive && showNewDot) {
      setShowNewDot(false);
      localStorage.setItem('aos_learn_new_badge', '0');
    }
  }, [isActive, path, showNewDot]);
  return (
    <Link
      to={path}
      onClick={onClick}
      aria-current={isActive ? 'page' : undefined}
      style={{ textDecoration: 'none', display: 'block', marginBottom: '1px' }}
      data-testid={testId}
    >
      <div
        className="sidebar-nav-glow"
        style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          padding: collapsed ? '10px' : '0 12px',
          borderRadius: '10px', justifyContent: collapsed ? 'center' : 'flex-start',
          background: isActive ? 'rgba(255,255,255,0.05)' : 'transparent',
          borderLeft: isActive ? '2px solid #E87722' : '2px solid transparent',
          marginLeft: isActive ? '-2px' : '0',
          color: isActive ? '#FFFFFF' : 'rgba(255,255,255,0.65)',
          fontWeight: isActive ? 600 : 400,
          transition: 'background 0.15s, color 0.15s',
          minHeight: '44px',
        }}
        onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = '#FFFFFF'; } }}
        onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; } }}
      >
        <span style={{ position: 'relative', lineHeight: 0 }}>
          <Icon size={16} aria-hidden="true" />
          {showNewDot && (
            <span style={{
              position: 'absolute', top: '-3px', right: '-4px',
              width: '6px', height: '6px', borderRadius: '50%',
              background: '#E87722', border: '1.5px solid #0B1426',
              animation: 'pulse 1.8s ease-in-out infinite',
            }} />
          )}
          {!showNewDot && badgeCount > 0 && collapsed && (
            <span style={{
              position: 'absolute', top: '-5px', right: '-6px',
              minWidth: '14px', height: '14px', borderRadius: '7px',
              background: '#DC2626', color: '#FFFFFF',
              fontSize: '8px', fontWeight: 800,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '0 3px', border: '1.5px solid #0B1426',
              fontFamily: 'Plus Jakarta Sans, sans-serif',
            }}
              data-testid="sidebar-badge-collapsed"
            >
              {badgeCount > 9 ? '9+' : badgeCount}
            </span>
          )}
        </span>
        {!collapsed && <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: isActive ? 600 : 400, fontSize: '13px', whiteSpace: 'nowrap', flex: 1 }}>{label}</span>}
        {!collapsed && showNewDot && (
          <span style={{ marginLeft: 'auto', fontSize: '9px', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#E87722', background: 'rgba(232,119,34,0.15)', borderRadius: '4px', padding: '2px 5px', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>NEW</span>
        )}
        {!collapsed && !showNewDot && badgeCount > 0 && (
          <span
            data-testid="sidebar-intro-badge"
            style={{
              marginLeft: 'auto', minWidth: '18px', height: '18px', borderRadius: '9px',
              background: '#DC2626', color: '#FFFFFF',
              fontSize: '10px', fontWeight: 800,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '0 4px', fontFamily: 'Plus Jakarta Sans, sans-serif',
              animation: 'pulse 1.8s ease-in-out infinite',
            }}
          >
            {badgeCount > 9 ? '9+' : badgeCount}
          </span>
        )}
      </div>
    </Link>
  );
}

// ── Section label shown above each nav group ──────────────────────────────────

function NavGroupLabel({ label, collapsed }) {
  if (collapsed) return <div style={{ height: '1px', background: 'rgba(255,255,255,0.08)', margin: '6px 10px' }} />;
  return (
    <div style={{
      fontSize: '9px', fontWeight: 700, letterSpacing: '0.1em',
      textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)',
      padding: '16px 12px 4px', fontFamily: 'Plus Jakarta Sans, sans-serif',
      userSelect: 'none',
    }}>
      {label}
    </div>
  );
}

function ThemeToggle({ collapsed }) {
  const { isDark, toggleTheme } = useTheme();
  return (
    <button
      onClick={toggleTheme}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={isDark ? 'Light mode' : 'Dark mode'}
      style={{
        display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
        padding: collapsed ? '10px' : '0 12px', borderRadius: '10px',
        background: 'transparent', border: 'none', cursor: 'pointer',
        color: 'rgba(255,255,255,0.4)', justifyContent: collapsed ? 'center' : 'flex-start',
        transition: 'all 0.15s', minHeight: '44px',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = '#FFFFFF'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; }}
      data-testid="theme-toggle-btn"
    >
      {isDark ? <Sun size={16} aria-hidden="true" /> : <Moon size={16} aria-hidden="true" />}
      {!collapsed && (
        <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '13px' }}>
          {isDark ? 'Light Mode' : 'Dark Mode'}
        </span>
      )}
    </button>
  );
}

// ── Main sidebar component ────────────────────────────────────────────────────

export default function Sidebar({ collapsed, setCollapsed }) {
  const { user, logout, authFetch } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const notifRef = useRef(null);

  // Fetch notifications periodically
  useEffect(() => {
    if (!user) return;
    const fetchNotifs = () => {
      authFetch('/notifications').then(r => {
        const data = r.data || [];
        setNotifications(data);
        setUnreadCount(data.filter(n => !n.read).length);
      }).catch(() => {});
    };
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 30000);
    return () => clearInterval(interval);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close notif panel on outside click
  useEffect(() => {
    const handler = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markAllRead = async () => {
    await authFetch('/notifications/read-all', { method: 'POST' }).catch(() => {});
    setUnreadCount(0);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const [pushEnabled, setPushEnabled] = useState(false);
  useEffect(() => {
    if ('Notification' in window) setPushEnabled(Notification.permission === 'granted');
  }, []);

  const handleEnablePush = async () => {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return;
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return;
      const reg = await navigator.serviceWorker.ready;
      const vapidRes = await authFetch('/notifications/vapid-public-key');
      const vapidKey = vapidRes.data?.public_key;
      if (!vapidKey) return;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(vapidKey),
      });
      await authFetch('/notifications/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: { subscription: sub.toJSON() },
      });
      setPushEnabled(true);
    } catch (e) {
      console.error('[Push] Subscribe error:', e);
    }
  };

  if (!user) return null;

  const groupedNavBase = getGroupedNav(user.role);
  const groupedNav = (user.profile || {}).is_super_admin
    ? { ...groupedNavBase, groups: [...groupedNavBase.groups, { label: 'FOUNDER', items: [{ path: '/marketing-insights', label: 'Marketing Insights', icon: BarChart2 }] }] }
    : groupedNavBase;
  const roleBadge = getRoleBadgeStyle(user.role);
  const initials = `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`;
  const profile = user.profile || {};

  // Badge count for case-manager intro requests (unread job-type notifications)
  const introBadgeCount = user.role === 'case_manager'
    ? notifications.filter(n => !n.read && n.type === 'job').length
    : 0;

  const handleLogout = () => { logout(); navigate('/'); };

  const SidebarContent = () => (
    <div style={{
      background: '#0B1426', borderRight: '1px solid rgba(255,255,255,0.06)',
      width: collapsed ? '72px' : '260px', height: '100vh',
      display: 'flex', flexDirection: 'column', transition: 'width 0.3s ease',
      position: 'sticky', top: 0, flexShrink: 0, overflowY: 'auto', overflowX: 'hidden',
    }}>
      {/* ── Orange accent bar ── */}
      <div style={{ height: '2px', background: '#E87722', width: '100%', flexShrink: 0 }} />

      {/* ── Top: Logo + user info ── */}
      <div style={{ padding: collapsed ? '16px 12px' : '16px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        {/* Logo + collapse toggle */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'space-between', marginBottom: '14px' }}>
          {!collapsed && (
            <Link to="/" style={{ textDecoration: 'none' }}>
              <img src="/logo.png" alt="AssistivOS" style={{ height: 26, width: 'auto', display: 'block' }} />
            </Link>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'rgba(255,255,255,0.5)', flexShrink: 0, transition: 'all 0.15s' }}
            data-testid="sidebar-toggle"
          >
            {collapsed ? <ChevronRight size={13} aria-hidden="true" /> : <ChevronLeft size={13} aria-hidden="true" />}
          </button>
        </div>

        {/* User avatar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <div style={{
            width: '32px', height: '32px', minWidth: '32px', borderRadius: '50%',
            background: '#E87722',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '12px',
            color: '#FFFFFF', flexShrink: 0,
          }}>
            {initials}
          </div>
          {!collapsed && (
            <div style={{ overflow: 'hidden' }}>
              <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '13px', color: '#FFFFFF', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user.first_name} {user.last_name}
              </div>
              <span style={{
                display: 'inline-block', marginTop: '3px',
                background: 'rgba(232,119,34,0.1)', color: '#E87722',
                border: '1px solid rgba(232,119,34,0.4)',
                borderRadius: '6px', padding: '1px 7px', fontSize: '9px',
                fontWeight: 700, fontFamily: 'Plus Jakarta Sans, sans-serif', letterSpacing: '0.04em',
              }}>
                {roleBadge.label.toUpperCase()}
              </span>
            </div>
          )}
        </div>

        {/* Credits badge (learner only) */}
        {user.role === 'learner' && !collapsed && (
          <div
            data-testid="sidebar-credits"
            style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '12px', marginBottom: '8px' }}
          >
            <p style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '22px', color: '#E87722', margin: '0 0 2px 0' }}
              data-testid="sidebar-credit-value">
              {(profile.credits || 0).toLocaleString()}
            </p>
            <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', margin: '0 0 2px 0', fontFamily: 'Inter, sans-serif' }}>credits earned</p>
            <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.55)', margin: 0 }} data-testid="sidebar-streak">
              🔥 {profile.streak_days || 0}-day streak
            </p>
          </div>
        )}

        {/* Notification Bell */}
        <div ref={notifRef} style={{ position: 'relative', marginTop: '4px' }}>
          <button onClick={() => setNotifOpen(o => !o)}
            style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: collapsed ? '8px' : '0 12px', borderRadius: '10px', background: 'transparent', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.5)', justifyContent: collapsed ? 'center' : 'flex-start', transition: 'all 0.15s', position: 'relative', minHeight: '44px' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = '#FFFFFF'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
            aria-label="Notifications" data-testid="notification-bell">
            <span style={{ position: 'relative' }}>
              <Bell size={16} />
              {unreadCount > 0 && (
                <span style={{ position: 'absolute', top: '-5px', right: '-5px', width: '16px', height: '16px', borderRadius: '50%', background: '#DC2626', color: '#FFFFFF', fontSize: '9px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="notif-badge">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </span>
            {!collapsed && <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '13px', color: 'rgba(255,255,255,0.65)' }}>Notifications</span>}
          </button>

          {notifOpen && (
            <div style={{ position: 'absolute', left: collapsed ? '72px' : '0', top: 0, width: '300px', background: '#FFFFFF', border: '1px solid var(--border)', borderRadius: '14px', boxShadow: '0 20px 48px rgba(0,0,0,0.2)', zIndex: 500, overflow: 'hidden' }}
              data-testid="notification-panel">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '14px', color: '#0F172A' }}>Notifications</span>
                {unreadCount > 0 && (
                  <button onClick={markAllRead} style={{ fontSize: '11px', color: '#B55210', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Mark all read</button>
                )}
              </div>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {notifications.length === 0 ? (
                  <div style={{ padding: '24px', textAlign: 'center', color: '#64748B', fontSize: '13px' }}>No notifications</div>
                ) : notifications.slice(0, 10).map(n => (
                  <div key={n.id} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', background: n.read ? 'transparent' : 'rgba(232,119,34,0.04)' }}>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                      {!n.read && <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#E87722', marginTop: '5px', flexShrink: 0 }} />}
                      <div style={{ flex: 1, marginLeft: n.read ? '16px' : '0' }}>
                        <p style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '13px', color: '#0F172A', margin: '0 0 2px 0' }}>{n.title}</p>
                        <p style={{ fontSize: '12px', color: '#475569', margin: '0 0 2px 0', lineHeight: 1.4 }}>{n.message}</p>
                        <p style={{ fontSize: '11px', color: '#64748B', margin: 0 }}>{new Date(n.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Navigation (grouped) ── */}
      <nav role="navigation" aria-label="Main navigation" style={{ flex: 1, padding: '8px 12px 4px', overflowY: 'auto' }}>
        {/* Pinned item (Dashboard) */}
        {groupedNav.pinned && (
          <SidebarNavItem
            path={groupedNav.pinned.path}
            label={groupedNav.pinned.label}
            icon={groupedNav.pinned.icon}
            exact={groupedNav.pinned.exact}
            collapsed={collapsed}
            onClick={() => {}}
          />
        )}

        {/* Section groups */}
        {groupedNav.groups.map((group, gIdx) => (
          <div key={group.label} style={{ marginTop: gIdx === 0 && groupedNav.pinned ? '4px' : '0' }}>
            <NavGroupLabel label={group.label} collapsed={collapsed} />
            {group.items.map(({ path, label, icon, exact }) => (
              <SidebarNavItem
                key={path}
                path={path}
                label={label}
                icon={icon}
                exact={exact}
                collapsed={collapsed}
                onClick={() => {}}
                badgeCount={path === '/jobs' ? introBadgeCount : 0}
              />
            ))}
          </div>
        ))}
      </nav>

      {/* ── Bottom: theme, push, logout ── */}
      <div style={{ padding: '8px 12px 16px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <ThemeToggle collapsed={collapsed} />

        {'Notification' in window && !pushEnabled && (
          <button
          onClick={handleEnablePush}
          title="Enable push notifications"
          data-testid="enable-push-btn"
          style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: collapsed ? '10px' : '0 12px', borderRadius: '10px', background: 'transparent', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', justifyContent: collapsed ? 'center' : 'flex-start', transition: 'all 0.15s', minHeight: '44px' }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = '#FFFFFF'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; }}>
            <BellRing size={16} aria-hidden="true" />
            {!collapsed && <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '13px' }}>Enable Notifications</span>}
          </button>
        )}

        <button
          onClick={handleLogout}
          style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: collapsed ? '10px' : '0 12px', borderRadius: '10px', background: 'transparent', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', justifyContent: collapsed ? 'center' : 'flex-start', transition: 'all 0.15s', minHeight: '44px' }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(220,38,38,0.15)'; e.currentTarget.style.color = '#FCA5A5'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; }}
          aria-label="Sign out"
          data-testid="logout-button"
        >
          <LogOut size={16} aria-hidden="true" />
          {!collapsed && <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '13px' }}>Sign Out</span>}
        </button>

        {!collapsed && (
          <div style={{ textAlign: 'center', marginTop: '10px', padding: '0 8px' }}>
            <div style={{ fontSize: '10px', color: 'var(--text-dim)', fontFamily: 'Inter, sans-serif', lineHeight: 1.4 }}>
              Powered by <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Tech Tradesmith Institute</span>
            </div>
            <div style={{ fontSize: '9px', color: '#444444', fontFamily: 'Inter, sans-serif', marginTop: '2px', fontStyle: 'italic' }}>
              Where Technology Meets Craft
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ position: 'sticky', top: 0, height: '100vh' }}>
      <SidebarContent />
    </div>
  );
}
